// Показ самих фотографий инженерного блока (портал 73): верификации, отчёты по заявкам,
// сканы актов.
//
// ЗАЧЕМ. До этого CRM показывала только РЕЕСТР: «5 файлов, 2,1 МБ, загружены 14 мая».
// Для верификации это терпимо — там весь чек-лист в данных. Для отчёта по заявке
// фотография часто и ЕСТЬ содержание отчёта: по ней видно, что именно сделали на судне,
// и строка «IMG_0042.jpg, 1,3 МБ» на этот вопрос не отвечает.
//
// ────────────────── ПОЧЕМУ У КАРТИНКИ НЕТ АДРЕСА ──────────────────
// Обычная галерея — это <img src="/api/…/photo/17">. Здесь так нельзя, и не из-за
// технических ограничений. Всё, что имеет адрес, однажды оказывается в пересланном
// сообщении, в истории браузера, в Referer — и открывается у того, у кого никакой сессии
// в CRM нет. Ровно это уже случилось со ссылками на чеки Яндекс.Такси: они открывались
// без авторизации, и поэтому раздел такси в зеркало не переносили вовсе (ПЛАН_остаток.md,
// В7.3). Фотографии судов — это и персональные данные (люди в кадре), и коммерческая
// информация заказчика.
//
// Поэтому байты приезжают ответом на обычный запрос к BFF с сессионной кукой, а адрес
// data: собирается здесь, в памяти вкладки. «Копировать ссылку на изображение» в этом
// случае копирует сами байты, а не право их получить, — переслать доступ невозможно.
//
// ────────────────── ПОЧЕМУ НЕ ГРУЗИМ ВСЁ СРАЗУ ──────────────────
// У верификации бывает три десятка снимков по 400 КБ, и «просто показать галерею»
// означало бы 12 МБ на открытие карточки — на судовом интернете это минуты. Поэтому
// снимок едет тогда, когда его попросили, а кнопка «показать все» знает общий вес и
// говорит его вслух. Отдельно: ядро не отдаёт файлы больше 16 МБ (p73_photo.py), и это
// видно заранее, до нажатия.
import { useState } from 'react'

import { адресФайла, useФайлP73 } from '../api/p73'
import type { ВидФайлаP73, ФайлP73, ФотоP73 } from '../api/p73'
import { размерФайла } from '../lib/p73'

/** Порог «показать все»: выше него грузим только по одной. 25 МБ — примерно минута на 3G. */
const ПОРОГ_ВСЕ = 25 * 1024 * 1024
/** Столько ядро не отдаёт (МАКС_БАЙТ в p73_photo.py). Сказать это надо ДО нажатия. */
const ПОТОЛОК_ЯДРА = 16 * 1024 * 1024

interface Props {
  вид: ВидФайлаP73
  файлы: ФотоP73[]
  /** Что написать, когда файлов нет. У акта и у отчёта это разные фразы. */
  пусто?: string
}

export function PhotoGallery({ вид, файлы, пусто }: Props) {
  // Множество, а не «открыт один»: человек сравнивает снимки между собой, и закрывать
  // предыдущий при открытии следующего — значит мешать ему это делать.
  const [открыты, setОткрыты] = useState<Set<number>>(new Set())
  const [крупно, setКрупно] = useState<number | null>(null)

  if (!файлы.length) {
    return <div className="empty"><div className="empty-title">{пусто ?? 'Фотографий к этой записи в ядре нет.'}</div></div>
  }

  const вес = файлы.reduce((s, ф) => s + (ф.size_bytes ?? 0), 0)
  // Вес известен не всегда: у сканов актов размер в зеркале не хранится вовсе.
  const весИзвестен = файлы.every((ф) => ф.size_bytes !== null && ф.size_bytes !== undefined)
  const всеСразуМожно = !весИзвестен || вес <= ПОРОГ_ВСЕ
  const открытьВсе = () => setОткрыты(new Set(файлы.map((ф) => ф.d1_id)))

  return (
    <>
      <div className="photo-actions">
        <button type="button" onClick={открытьВсе}
                disabled={открыты.size === файлы.length || !всеСразуМожно}>
          показать все ({файлы.length}{весИзвестен ? `, ${размерФайла(вес)}` : ''})
        </button>
        {открыты.size > 0 && (
          <button type="button" onClick={() => { setОткрыты(new Set()); setКрупно(null) }}>
            свернуть
          </button>
        )}
        {!всеСразуМожно && (
          <span className="hint">
            Все разом — это {размерФайла(вес)}; на судовом интернете это долго, поэтому
            открывайте по одной.
          </span>
        )}
      </div>

      <div className="photo-grid">
        {файлы.map((ф) => (
          <Плитка
            key={ф.d1_id}
            вид={вид}
            фото={ф}
            открыт={открыты.has(ф.d1_id)}
            крупно={крупно === ф.d1_id}
            открыть={() => setОткрыты((s) => new Set(s).add(ф.d1_id))}
            переключитьКрупно={() => setКрупно((k) => (k === ф.d1_id ? null : ф.d1_id))}
          />
        ))}
      </div>

      <div className="hint" style={{ marginTop: 8 }}>
        Файлы лежат в хранилище портала 73 — ядро читает их своим ключом «только чтение» и
        отдаёт вам через вход в CRM. Отдельной ссылки на фотографию не существует: открыть
        её без входа в CRM нельзя, переслать «ссылку на картинку» — тоже.
      </div>
    </>
  )
}

interface ПлиткаProps {
  вид: ВидФайлаP73
  фото: ФотоP73
  открыт: boolean
  крупно: boolean
  открыть: () => void
  переключитьКрупно: () => void
}

function Плитка({ вид, фото, открыт, крупно, открыть, переключитьКрупно }: ПлиткаProps) {
  const велик = (фото.size_bytes ?? 0) > ПОТОЛОК_ЯДРА
  const файл = useФайлP73(вид, фото.d1_id, открыт && !велик)
  const подпись = фото.label || `#${фото.d1_id}`

  return (
    <figure className={`photo${крупно ? ' big' : ''}`}>
      {!открыт ? (
        <button type="button" className="photo-open" onClick={открыть} disabled={велик}>
          <span className="photo-open-t">{велик ? 'слишком большой' : 'показать'}</span>
          <span className="photo-open-s">{размерФайла(фото.size_bytes)}</span>
        </button>
      ) : (
        <Содержимое файл={файл} подпись={подпись} крупно={крупно}
                    переключитьКрупно={переключитьКрупно} />
      )}
      <figcaption>
        {фото.section_key && <span className="mono muted">{фото.section_key} · </span>}
        {подпись}
        {/* Скан формы Регистра портал помечает маркером формы, а не именем файла —
            иначе «form_4.1.1» выглядит опечаткой. */}
        {подпись.startsWith('form_') && (
          <span className="muted"> — скан формы Регистра</span>
        )}
        {велик && (
          <div className="muted">
            {размерФайла(фото.size_bytes)} — больше {размерФайла(ПОТОЛОК_ЯДРА)}, которые
            отдаёт ядро. Этот файл открывается только на портале 73.
          </div>
        )}
      </figcaption>
    </figure>
  )
}

function Содержимое({ файл, подпись, крупно, переключитьКрупно }: {
  файл: ReturnType<typeof useФайлP73>
  подпись: string
  крупно: boolean
  переключитьКрупно: () => void
}) {
  if (файл.isLoading) return <div className="photo-box"><div className="skeleton" style={{ height: 120 }} /></div>
  if (файл.error) {
    // Отказ хранилища и «фотографии нет» — разные вещи, и путать их нельзя: инженер решит,
    // что снимков не делали. Текст причины приходит от ядра (503 storage_unavailable).
    return (
      <div className="photo-box photo-err">
        Не получилось прочитать файл: {файл.error instanceof Error ? файл.error.message : 'ошибка'}.
        Это не значит, что фотографии нет — она есть в реестре ниже.
      </div>
    )
  }
  const ф: ФайлP73 | undefined = файл.data
  if (!ф) return <div className="photo-box photo-err">Файл не пришёл.</div>

  if (!ф.inline) {
    // Не картинка (у актов это .pdf и .docx). Показывать нечего — даём скачать. Тип ядро
    // выдало по своему списку, исполняемых типов в нём нет.
    return (
      <div className="photo-box">
        <a className="photo-dl" href={адресФайла(ф)} download={подпись}>
          скачать {ф.content_type.includes('pdf') ? 'PDF' : 'файл'} · {размерФайла(ф.size)}
        </a>
      </div>
    )
  }
  return (
    <button type="button" className="photo-box photo-img" onClick={переключитьКрупно}
            title={крупно ? 'уменьшить' : 'во всю ширину'}>
      <img src={адресФайла(ф)} alt={подпись} loading="lazy" />
    </button>
  )
}
